import React, { Component } from 'react';
import { connect } from 'react-redux';
import Auth from '../utils/Auth';
import { setPrivacyPolicyDialogOpened } from '../menu/menu.actions';
import {
    getConsent, getStoredConsentRaw, saveConsent, onTrackingChange
} from '../tracking/clientTracking';

/**
 * Small cookie bubble in the bottom-LEFT corner of the menu, for every place
 * (never full width - the bottom has order buttons, the bottom-right the call button).
 *
 *  Every place gets the same real choice (GDPR): "Приемам" (analytics + marketing) /
 *  "Само необходими" / "Настройки" (two switches); ✕ = only necessary. Nothing tracks before it -
 *  TabL's own pixel / Google tag, the place's own GA4 / pixel / Ads / custom code and its GTM
 *  (tracking/clientTracking.js). Afterwards a tiny cookie button bottom-left reopens the choice,
 *  so consent can be withdrawn at any time.
 *
 * The choice is a cookie per place (tabl_cc_<placeId>); the server reads it too, so the
 * Conversions API respects it (metaCapiService.getConsentFromRequest).
 * Never shown in kiosk / tablet mode.
 */
const TEXTS = {
    bg: {
        gtm: 'Използваме бисквитки, за да работи менюто, а с ваше съгласие – и за анализ на посещенията и реклама (Google, Meta).',
        more: 'Повече',
        accept: 'Приемам',
        necessary: 'Само необходими',
        settings: 'Настройки',
        save: 'Запази',
        analytics: 'Анализ (Google Analytics)',
        marketing: 'Маркетинг (Meta, Google реклами)',
        reopen: 'Бисквитки',
        close: 'Затвори',
    },
    en: {
        gtm: 'We use cookies to run the menu and, with your consent, for visit analytics and advertising (Google, Meta).',
        more: 'More',
        accept: 'Accept',
        necessary: 'Only necessary',
        settings: 'Settings',
        save: 'Save',
        analytics: 'Analytics (Google Analytics)',
        marketing: 'Marketing (Meta, Google ads)',
        reopen: 'Cookies',
        close: 'Close',
    }
};

// Bottom-LEFT corner only, raised above the bottom edge: the bottom of the screen has the
// full-width order/add buttons and the bottom-right corner the call / speed-dial button.
// The bubble never reaches the right side (width leaves ~96px free there).
const CORNER_LEFT = 10;
const CORNER_BOTTOM = 72;
// Below MUI dialogs (1300): an open cart / order modal always covers it.
const Z_BUBBLE = 1250;
const Z_CIRCLE = 1200;

const styles = {
    bar: {
        position: 'fixed', left: CORNER_LEFT, bottom: CORNER_BOTTOM, zIndex: Z_BUBBLE,
        width: 'min(280px, calc(100vw - 96px))', boxSizing: 'border-box',
        background: 'rgba(33, 33, 33, 0.96)', color: '#fff',
        fontSize: 12.5, lineHeight: 1.4, textAlign: 'left',
        padding: '10px 30px 10px 12px', borderRadius: 12,
        display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8,
        boxShadow: '0 4px 14px rgba(0,0,0,0.35)',
    },
    text: { margin: 0 },
    link: { color: '#fff', textDecoration: 'underline', cursor: 'pointer', background: 'none', border: 0, padding: 0, font: 'inherit', marginLeft: 4 },
    buttons: { display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' },
    primary: { background: '#fff', color: '#222', border: 0, borderRadius: 6, padding: '6px 14px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' },
    secondary: { background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.6)', borderRadius: 6, padding: '5px 10px', fontSize: 12.5, cursor: 'pointer' },
    close: { position: 'absolute', right: 4, top: 4, background: 'none', border: 0, color: '#fff', fontSize: 20, lineHeight: 1, cursor: 'pointer', padding: 4 },
    switches: { display: 'flex', flexDirection: 'column', gap: 6 },
    switchLabel: { display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' },
    reopen: {
        position: 'fixed', left: CORNER_LEFT, bottom: CORNER_BOTTOM, zIndex: Z_CIRCLE,
        width: 34, height: 34, borderRadius: '50%', border: 0,
        background: 'rgba(33, 33, 33, 0.6)', color: '#fff', fontSize: 17, lineHeight: '34px',
        padding: 0, cursor: 'pointer', textAlign: 'center',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
    },
};

class CookieConsentBar extends Component {
    state = {
        dismissed: false,
        reopened: false,
        showSettings: false,
        analytics: false,
        marketing: false,
    };

    componentDidMount() {
        this.unsubscribe = onTrackingChange(() => this.forceUpdate());
    }

    componentWillUnmount() {
        if (this.unsubscribe) this.unsubscribe();
    }

    getTexts() {
        const lang = String(this.props.currLang || Auth.getCurrLang() || 'bg').toLowerCase();
        return TEXTS[lang] || TEXTS.en;
    }

    choose = (analytics, marketing) => {
        const placeId = Auth.getOpenedPlace();
        saveConsent(placeId, { analytics, marketing });
        this.setState({ dismissed: true, reopened: false, showSettings: false });
    };

    // ✕ on the first bar = only necessary; ✕ on a reopened bar just hides it (keeps the choice).
    closeGtmBar = () => {
        if (this.state.reopened) {
            this.setState({ reopened: false, showSettings: false });
            return;
        }
        this.choose(false, false);
    };

    openSettings = () => {
        const c = getConsent();
        this.setState({ showSettings: true, analytics: c.analytics, marketing: c.marketing });
    };

    render() {
        const { settings } = this.props;
        const settingsLoaded = settings && Object.keys(settings).length > 0;
        if (!settingsLoaded) return null;
        if (Auth.isKioskMode() || Auth.isTabletMode()) return null;

        const placeId = Auth.getOpenedPlace();
        if (!placeId) return null;

        const t = this.getTexts();
        // 'ack' = the old "OK" bar from before the real choice -> ask again.
        const raw = getStoredConsentRaw(placeId);
        const hasChoice = !!raw && raw !== 'ack';

        if ((hasChoice || this.state.dismissed) && !this.state.reopened) {
            // Withdrawing consent must be as easy as giving it.
            return (
                <button type="button" style={styles.reopen} title={t.reopen} aria-label={t.reopen}
                    onClick={() => this.setState({ reopened: true, showSettings: false })}>
                    🍪
                </button>
            );
        }

        return (
            <div style={styles.bar} role="region" aria-label={t.reopen}>
                <p style={styles.text}>
                    {t.gtm}
                    <button type="button" style={styles.link} onClick={() => this.props.setPrivacyPolicyDialogOpened(true)}>{t.more}</button>
                </p>

                {this.state.showSettings ? (
                    <React.Fragment>
                        <div style={styles.switches}>
                            <label style={styles.switchLabel}>
                                <input type="checkbox" checked={this.state.analytics} onChange={e => this.setState({ analytics: e.target.checked })} />
                                {t.analytics}
                            </label>
                            <label style={styles.switchLabel}>
                                <input type="checkbox" checked={this.state.marketing} onChange={e => this.setState({ marketing: e.target.checked })} />
                                {t.marketing}
                            </label>
                        </div>
                        <div style={styles.buttons}>
                            <button type="button" style={styles.primary} onClick={() => this.choose(this.state.analytics, this.state.marketing)}>{t.save}</button>
                        </div>
                    </React.Fragment>
                ) : (
                    <div style={styles.buttons}>
                        <button type="button" style={styles.primary} onClick={() => this.choose(true, true)}>{t.accept}</button>
                        <button type="button" style={styles.secondary} onClick={() => this.choose(false, false)}>{t.necessary}</button>
                        <button type="button" style={styles.link} onClick={this.openSettings}>{t.settings}</button>
                    </div>
                )}

                <button type="button" style={styles.close} aria-label={t.close} title={t.close} onClick={this.closeGtmBar}>×</button>
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    settings: state.settings.settings,
    currLang: state.lang && state.lang.currLang,
});

export default connect(mapStateToProps, { setPrivacyPolicyDialogOpened })(CookieConsentBar);
